import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import {
  FiSearch,
  FiUpload,
  FiFile,
  FiFolder,
  FiGithub,
  FiCode,
  FiDollarSign,
  FiEdit3,
  FiTrash2,
  FiSave,
  FiX,
  FiDownload,
  FiLock,
  FiCreditCard,
  FiImage,
  FiFileText,
  FiArrowUpRight,
  FiMail,
  FiTerminal,
} from "react-icons/fi";
import {
  FaGithub,
  FaLinkedin,
  FaInstagram,
  FaWhatsapp,
} from "react-icons/fa";
import { SiLeetcode } from "react-icons/si";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import QRCode from "react-qr-code";
import Swal from "sweetalert2";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css"; // design system: tokens, buttons, grain, .kk-progress
import "./Code.css"; // everything unique to this page

/* ═══════════════ DATA (presentational only — logic untouched) ═══════════════ */

const ADMIN_EMAIL = "karanksxxx@gmail.com";
const CODE_TABS = ["All", "Java", "React", "JavaScript", "DSA", "Others"];

// Local poster images used when a remote preview URL fails to load.
const FALLBACK_IMG = [
  "/img/proj-notes.jpg",
  "/img/proj-products.jpg",
  "/img/proj-listing.jpg",
  "/img/proj-realtycore.jpg",
];

// Demo items for preview when Firebase is not configured.
// (In your project with real `db`, these are never used — onSnapshot loads Firestore.)
const DEMO_UPLOADS = [
  {
    id: "demo-1",
    title: "Java - Factorial Program",
    uploadPic:
      "https://images.unsplash.com/photo-1517694712202-14dd9538aa97?q=80&w=600&auto=format&fit=crop",
    paymentAmount: null,
    githubURL: "https://github.com/Karanks1436",
    files: [],
  },
  {
    id: "demo-2",
    title: "React Portfolio Website",
    uploadPic:
      "https://images.unsplash.com/photo-1633356122544-f134324a6cee?q=80&w=600&auto=format&fit=crop",
    paymentAmount: "99.00",
    files: [
      {
        name: "App.jsx",
        path: "src/App.jsx",
        data:
          "data:text/plain;charset=utf-8," +
          encodeURIComponent("export default function App(){ return <h1>Hello</h1> }"),
      },
      {
        name: "index.css",
        path: "src/index.css",
        data: "data:text/plain;charset=utf-8," + encodeURIComponent("body{ background:#040918; }"),
      },
    ],
  },
  {
    id: "demo-3",
    title: "JavaScript DSA Solutions",
    uploadPic:
      "https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=600&auto=format&fit=crop",
    paymentAmount: null,
    files: [
      {
        name: "dsa-notes.txt",
        path: "dsa-notes.txt",
        data:
          "data:text/plain;charset=utf-8," +
          encodeURIComponent("DSA quick revision notes..."),
      },
    ],
  },
];

const SOCIALS = [
  { icon: <FaGithub />, href: "https://github.com/Karanks1436", label: "GitHub" },
  { icon: <FaLinkedin />, href: "https://linkedin.com", label: "LinkedIn" },
  { icon: <FaInstagram />, href: "https://www.instagram.com/karanks1436", label: "Instagram" },
  { icon: <FiMail />, href: "mailto:karanksxxx@gmail.com", label: "Email" },
  { icon: <FaWhatsapp />, href: "https://wa.me/919041429065", label: "WhatsApp" },
  { icon: <SiLeetcode />, href: "https://leetcode.com/u/karanks1436/", label: "LeetCode" },
];

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

// ─── Floating Particles ────────────────────────────────────────
function CodeParticles() {
  const parts = useRef(
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="cd-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="cd-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.08, 0.4, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

// ─── Code Card ─────────────────────────────────────────────────
function CodeCard({
  note,
  index,
  user,
  isAdmin,
  editModeId,
  editTitle,
  editPic,
  editPrice,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onPay,
  onEditChange,
  onOpenFolder,
}) {
  const isEditing = editModeId === note.id;
  const isLoggedIn = !!user;
  const hasPrice = note.paymentAmount && parseFloat(note.paymentAmount) > 0;
  const hasGithub = !!note.githubURL;
  const hasMultipleFiles = !hasGithub && note.files?.length > 1;
  const hasSingleFile = !hasGithub && note.files?.length === 1;
  const localFallback = FALLBACK_IMG[index % FALLBACK_IMG.length];

  return (
    <motion.div
      className={`cd-card ${isEditing ? "is-editing" : ""}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ delay: (index % 3) * 0.08, duration: 0.5 }}
      whileHover={{ y: -6 }}
      layout
    >
      {/* Cover */}
      <div className="cd-card__cover">
        <img
          src={note.uploadPic || note.img || localFallback}
          alt=""
          loading="lazy"
          onError={(e) => {
            if (!e.currentTarget.dataset.fb) {
              e.currentTarget.dataset.fb = "1";
              e.currentTarget.src = localFallback;
            }
          }}
        />
        <span className="cd-card__shade" aria-hidden="true" />

        <span className="cd-card__badge cd-card__badge--type">
          <FiCode /> Code
        </span>

        <div className="cd-card__badges">
          {hasPrice && (
            <span className="cd-card__badge cd-card__badge--price">
              ₹{parseFloat(note.paymentAmount).toFixed(2)}
            </span>
          )}
          {hasGithub && (
            <span className="cd-card__badge cd-card__badge--gh">
              <FiGithub /> Repo
            </span>
          )}
          {hasMultipleFiles && (
            <span className="cd-card__badge cd-card__badge--files">
              <FiFolder /> {note.files.length} files
            </span>
          )}
        </div>

        <span className="cd-card__no">{String(index + 1).padStart(2, "0")}</span>
      </div>

      {/* Body */}
      <div className="cd-card__body">
        {isEditing ? (
          <div className="cd-edit">
            <label className="cd-field">
              <span>Title</span>
              <input
                type="text"
                value={editTitle}
                onChange={(e) => onEditChange("title", e.target.value)}
                placeholder="Item title"
              />
            </label>
            <label className="cd-field">
              <span>Preview image</span>
              <input
                type="text"
                value={editPic}
                onChange={(e) => onEditChange("pic", e.target.value)}
                placeholder="Image URL"
              />
            </label>
            <label className="cd-field">
              <span>Price (₹)</span>
              <input
                type="number"
                value={editPrice}
                onChange={(e) => onEditChange("price", e.target.value)}
                placeholder="0.00"
              />
            </label>
            <div className="cd-edit__btns">
              <motion.button
                className="kk-btn"
                onClick={() => onSaveEdit(note.id)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FiSave /> Save
              </motion.button>
              <motion.button
                className="kk-btn kk-btn--ghost"
                onClick={onCancelEdit}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                <FiX /> Cancel
              </motion.button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="cd-card__title">{note.title}</h3>
            <p className="cd-card__desc">
              {hasGithub
                ? "Source on GitHub — open the repo."
                : hasMultipleFiles
                ? `Folder of ${note.files.length} files, ready to download.`
                : hasSingleFile
                ? note.files[0].name
                : "No files attached."}
            </p>

            <div className="cd-card__actions">
              {/* GitHub link */}
              {hasGithub && isLoggedIn && (
                <motion.a
                  href={note.githubURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="kk-btn cd-btn-main"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiGithub /> View code
                </motion.a>
              )}

              {/* Folder view */}
              {hasMultipleFiles && isLoggedIn && (
                <motion.button
                  className="kk-btn cd-btn-main"
                  onClick={() => onOpenFolder(note.files, note.title)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiFolder /> Open folder ({note.files.length})
                </motion.button>
              )}

              {/* Single file */}
              {hasSingleFile && isLoggedIn && (
                <motion.a
                  href={note.files[0].data}
                  download={note.files[0].name}
                  className="kk-btn cd-btn-main"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiDownload /> {note.files[0].name}
                </motion.a>
              )}

              {/* Login required */}
              {!isLoggedIn && (
                <button className="kk-btn kk-btn--ghost cd-btn-main is-locked" disabled>
                  <FiLock /> Login required
                </button>
              )}

              {/* Pay */}
              {!isAdmin && hasPrice && (
                <motion.button
                  className="kk-btn cd-btn-pay"
                  onClick={() => onPay(note)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FiCreditCard /> ₹{parseFloat(note.paymentAmount).toFixed(2)}
                </motion.button>
              )}

              {/* Admin */}
              {isAdmin && !isEditing && (
                <div className="cd-card__admin">
                  <motion.button
                    className="cd-icon-btn"
                    onClick={() => onEdit(note)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Edit"
                  >
                    <FiEdit3 />
                  </motion.button>
                  <motion.button
                    className="cd-icon-btn is-danger"
                    onClick={() => onDelete(note.id)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Delete"
                  >
                    <FiTrash2 />
                  </motion.button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

// ─── Admin Upload Form ─────────────────────────────────────────
function AdminUploadForm({
  fileTitle,
  setFileTitle,
  filePreview,
  onFileChange,
  uploadPic,
  setUploadPic,
  paymentAmount,
  setPaymentAmount,
  githubURL,
  setGithubURL,
  onUpload,
}) {
  return (
    <motion.section
      className="cd-upload"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="cd-upload__head">
        <span className="kk-eyebrow">Admin — Archive</span>
        <h2 className="kk-h2">
          Upload new <span className="kk-serif kk-acid">code</span>
        </h2>
        <p className="cd-upload__note">
          Files, a whole folder, or just a GitHub link. Price is optional — leave it empty for free.
        </p>
      </div>

      <div className="cd-form">
        <div className="cd-form__row">
          <label className="cd-field" style={{ flex: 1 }}>
            <span>
              <FiFileText /> Title
            </span>
            <input
              type="text"
              value={fileTitle}
              onChange={(e) => setFileTitle(e.target.value)}
              placeholder="Project title"
            />
          </label>
          <label className="cd-field" style={{ flex: 1 }}>
            <span>
              <FiDollarSign /> Price (₹) — optional
            </span>
            <input
              type="number"
              min="1"
              step="0.01"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              placeholder="0.00"
            />
          </label>
        </div>

        <div className="cd-form__row">
          <div className="cd-field" style={{ flex: 1 }}>
            <span>
              <FiFolder /> Files / folder
            </span>
            <div className="cd-file-row">
              <input
                type="file"
                onChange={onFileChange}
                id="code-files"
                multiple
                webkitdirectory="true"
                className="cd-file-input"
              />
              <label htmlFor="code-files" className="kk-btn kk-btn--ghost">
                <FiFolder /> Choose files
              </label>
              {filePreview && (
                <span className="cd-file-status">
                  <FiFile /> {filePreview.length} file{filePreview.length > 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          <label className="cd-field" style={{ flex: 1 }}>
            <span>
              <FiImage /> Preview image URL
            </span>
            <input
              type="text"
              value={uploadPic}
              onChange={(e) => setUploadPic(e.target.value)}
              placeholder="https://..."
            />
          </label>
        </div>

        {filePreview && filePreview.length > 0 && (
          <div className="cd-file-list">
            {filePreview.slice(0, 5).map((f, idx) => (
              <div key={idx} className="cd-file-item">
                <FiFile /> {f.path}
              </div>
            ))}
            {filePreview.length > 5 && (
              <div className="cd-file-more">+{filePreview.length - 5} more files</div>
            )}
          </div>
        )}

        <label className="cd-field">
          <span>
            <FiGithub /> GitHub URL — optional
          </span>
          <input
            type="text"
            value={githubURL}
            onChange={(e) => setGithubURL(e.target.value)}
            placeholder="https://github.com/username/repo"
          />
        </label>

        <div className="cd-submit-row">
          <motion.button
            className="kk-btn cd-submit"
            onClick={onUpload}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiUpload /> Upload code
          </motion.button>
          <span className="cd-submit__hint">
            <FiTerminal /> Stored in the <code>Codes</code> collection
          </span>
        </div>
      </div>
    </motion.section>
  );
}

// ─── Folder Modal ──────────────────────────────────────────────
function FolderModal({ files, title, onClose }) {
  const downloadAll = () => {
    files.forEach((f) => {
      const link = document.createElement("a");
      link.href = f.data;
      link.download = f.name;
      link.click();
    });
  };

  return (
    <motion.div
      className="cd-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="cd-modal"
        initial={{ scale: 0.94, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.94, y: 30, opacity: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cd-modal__head">
          <div>
            <span className="kk-eyebrow">Folder</span>
            <h2>{title}</h2>
          </div>
          <motion.button
            className="cd-modal__x"
            onClick={onClose}
            whileHover={{ rotate: 90 }}
            aria-label="Close"
          >
            <FiX />
          </motion.button>
        </div>

        <div className="cd-modal__body">
          <div className="cd-folder__meta">
            <FiFolder /> {files.length} files in folder
          </div>

          <motion.button
            className="kk-btn cd-download-all"
            onClick={downloadAll}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <FiDownload /> Download all ({files.length} files)
          </motion.button>

          <div className="cd-folder-list">
            {files.map((f, idx) => (
              <motion.a
                key={idx}
                href={f.data}
                download={f.name}
                className="cd-folder-file"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <FiFile />
                <span>{f.name}</span>
                <FiDownload />
              </motion.a>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── QR Payment Modal ──────────────────────────────────────────
function QRPaymentModal({ note, onClose }) {
  const amount = parseFloat(note.paymentAmount);
  const upiLink = `upi://pay?pa=karanksxxx-1@oksbi&pn=Karan%20Kumar&am=${amount.toFixed(2)}&cu=INR`;

  return (
    <motion.div
      className="cd-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="cd-qr"
        initial={{ scale: 0.92, y: 30, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.92, y: 30, opacity: 0 }}
        transition={{ type: "spring", damping: 26, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cd-qr__head">
          <div>
            <span className="kk-eyebrow">UPI payment</span>
            <h2>
              Scan &amp; pay ₹{amount.toFixed(2)}
            </h2>
          </div>
          <motion.button
            className="cd-qr__x"
            onClick={onClose}
            whileHover={{ rotate: 90 }}
            aria-label="Close"
          >
            <FiX />
          </motion.button>
        </div>

        <div className="cd-qr__body">
          <div className="cd-qr__box">
            <QRCode value={upiLink} size={190} bgColor="#0a0a0c" fgColor="#e9efff" level="H" />
            <span className="cd-qr__corner tl" />
            <span className="cd-qr__corner tr" />
            <span className="cd-qr__corner bl" />
            <span className="cd-qr__corner br" />
          </div>

          <p className="cd-qr__amt">₹{amount.toFixed(2)}</p>
          <p className="cd-qr__merchant">Karan Kumar</p>
          <p className="cd-qr__upi">karanksxxx-1@oksbi</p>
          <p className="cd-qr__note">Paying for: {note.title}</p>

          <a className="kk-btn kk-btn--ghost cd-qr__upi-btn" href={upiLink}>
            <FiArrowUpRight /> Open UPI app
          </a>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Empty State ───────────────────────────────────────────────
function EmptyState({ isLoggedIn, searchQuery }) {
  return (
    <motion.div className="cd-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="cd-empty__ic">
        <FiCode />
      </div>
      <h3>{searchQuery ? "No code found" : isLoggedIn ? "Archive is empty" : "Login required"}</h3>
      <p>
        {searchQuery ? (
          <>
            No results for &ldquo;<strong>{searchQuery}</strong>&rdquo; — try another keyword.
          </>
        ) : isLoggedIn ? (
          "Code items will appear here once added."
        ) : (
          "Please login to access the code files."
        )}
      </p>
    </motion.div>
  );
}

// ─── Main Code Component ───────────────────────────────────────
// LOGIC: identical to your original — same state, same handlers, same
// Firebase calls. Only styling/classes, parallax and preview fallback added.
export default function Code() {
  const rootRef = useRef(null);
  const [activeTab, setActiveTab] = useState("All");
  const [user, setUser] = useState(null);
  const [fileTitle, setFileTitle] = useState("");
  const [filePreview, setFilePreview] = useState(null);
  const [uploadPic, setUploadPic] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [uploads, setUploads] = useState([]);
  const [showPaymentQR, setShowPaymentQR] = useState(false);
  const [currentPaymentNote, setCurrentPaymentNote] = useState(null);
  const [editModeId, setEditModeId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editPic, setEditPic] = useState("");
  const [editPrice, setEditPrice] = useState("");
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderModalFiles, setFolderModalFiles] = useState([]);
  const [folderTitle, setFolderTitle] = useState("");
  const [githubURL, setGithubURL] = useState("");
  const [loading, setLoading] = useState(true);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  useParallax(rootRef);

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // Preview-safe refs: identical to collection(db, ...) when db exists.
  const notesRef = useMemo(() => {
    try {
      return db ? collection(db, "Codes") : null;
    } catch {
      return null;
    }
  }, []);
  const searchRef = useMemo(() => {
    try {
      return db ? collection(db, "Searches") : null;
    } catch {
      return null;
    }
  }, []);
  const isAdmin = user?.Email === ADMIN_EMAIL;

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user"));
    if (userData) setUser(userData);

    if (!notesRef) {
      const t = setTimeout(() => {
        setUploads(DEMO_UPLOADS);
        setLoading(false);
      }, 600);
      return () => clearTimeout(t);
    }
    const unsubscribe = onSnapshot(notesRef, (snapshot) => {
      setUploads(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [notesRef]);

  const filteredNotes = uploads.filter((note) => {
    const matchSearch = note.title.toLowerCase().includes(searchQuery.toLowerCase());
    if (activeTab === "All") return matchSearch;
    if (activeTab === "Others") {
      const known = ["java", "react", "javascript", "dsa"];
      const inTitle = known.some((k) => note.title.toLowerCase().includes(k));
      const inCat = known.includes((note.category || "").toLowerCase());
      return matchSearch && !inTitle && !inCat;
    }
    const t = activeTab.toLowerCase();
    return (
      matchSearch &&
      (note.title.toLowerCase().includes(t) || (note.category || "").toLowerCase() === t)
    );
  });

  const generateUPIURL = (amount) => {
    if (isNaN(amount) || amount <= 0) return "";
    return `upi://pay?pa=karanksxxx-1@oksbi&pn=Karan%20Kumar&am=${amount.toFixed(2)}&cu=INR`;
  };

  const handlePayNow = (note) => {
    if (!note.paymentAmount || parseFloat(note.paymentAmount) <= 0) {
      Swal.fire({
        title: "Invalid Amount",
        text: "No valid payment amount set.",
        icon: "warning",
        background: "#0a0a0c",
        color: "#f3f3f5",
      });
      return;
    }
    setCurrentPaymentNote(note);
    if (isMobile) {
      localStorage.setItem("paymentInProgress", "true");
      window.location.href = generateUPIURL(parseFloat(note.paymentAmount));
    } else {
      setShowPaymentQR(true);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const filePromises = files.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve({
              name: file.name,
              path: file.webkitRelativePath || file.name,
              data: reader.result,
            });
          reader.onerror = reject;
          reader.readAsDataURL(file);
        })
    );

    Promise.all(filePromises)
      .then((results) => setFilePreview(results))
      .catch((err) => console.error("Error reading files:", err));

    setFileTitle(files.length === 1 ? files[0].name : `Multiple Files (${files.length})`);
  };

  const handleFileUpload = async () => {
    if (!isAdmin) {
      Swal.fire({
        title: "Access Denied",
        text: "Only admin can upload.",
        icon: "error",
        background: "#0a0a0c",
        color: "#f3f3f5",
      });
      return;
    }
    if ((!filePreview || filePreview.length === 0 || !fileTitle) && !githubURL) {
      Swal.fire({
        title: "Missing Fields",
        text: "Select files or provide a GitHub URL.",
        icon: "warning",
        background: "#0a0a0c",
        color: "#f3f3f5",
      });
      return;
    }

    const newUpload = {
      title: fileTitle,
      files: filePreview || [],
      uploadPic: uploadPic || "",
      paymentAmount: paymentAmount ? parseFloat(paymentAmount).toFixed(2) : null,
      timestamp: serverTimestamp(),
    };

    if (githubURL.trim()) {
      if (!githubURL.startsWith("https://github.com/")) {
        Swal.fire({
          title: "Invalid URL",
          text: "Enter a full GitHub URL.",
          icon: "warning",
          background: "#0a0a0c",
          color: "#f3f3f5",
        });
        return;
      }
      newUpload.githubURL = githubURL.trim();
      newUpload.files = [];
    }

    if (!notesRef) {
      setUploads((p) => [
        { ...newUpload, id: "local-" + Date.now(), timestamp: new Date().toISOString() },
        ...p,
      ]);
      Swal.fire({
        title: "Uploaded!",
        text: "Code added successfully.",
        icon: "success",
        background: "#0a0a0c",
        color: "#f3f3f5",
      });
      setFileTitle("");
      setFilePreview(null);
      setUploadPic("");
      setPaymentAmount("");
      setGithubURL("");
      return;
    }
    await addDoc(notesRef, newUpload);
    Swal.fire({
      title: "Uploaded!",
      text: "Code added successfully.",
      icon: "success",
      background: "#0a0a0c",
      color: "#f3f3f5",
    });
    setFileTitle("");
    setFilePreview(null);
    setUploadPic("");
    setPaymentAmount("");
    setGithubURL("");
  };

  const deleteUpload = async (id) => {
    if (!isAdmin) return;
    const result = await Swal.fire({
      title: "Delete Code?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f43f5e",
      cancelButtonColor: "#3b4a6b",
      confirmButtonText: "Delete",
      background: "#0a0a0c",
      color: "#f3f3f5",
    });
    if (result.isConfirmed) {
      if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
        setUploads((p) => p.filter((u) => u.id !== id));
        Swal.fire({
          title: "Deleted!",
          text: "Code removed.",
          icon: "success",
          background: "#0a0a0c",
          color: "#f3f3f5",
        });
        return;
      }
      await deleteDoc(doc(db, "Codes", id));
      Swal.fire({
        title: "Deleted!",
        text: "Code removed.",
        icon: "success",
        background: "#0a0a0c",
        color: "#f3f3f5",
      });
    }
  };

  const handleEdit = (note) => {
    setEditModeId(note.id);
    setEditTitle(note.title);
    setEditPic(note.uploadPic || "");
    setEditPrice(note.paymentAmount || "");
  };

  const handleEditChange = (field, value) => {
    if (field === "title") setEditTitle(value);
    if (field === "pic") setEditPic(value);
    if (field === "price") setEditPrice(value);
  };

  const handleSaveEdit = async (id) => {
    if (!db || String(id).startsWith("demo-") || String(id).startsWith("local-")) {
      setUploads((p) =>
        p.map((u) =>
          u.id === id
            ? {
                ...u,
                title: editTitle,
                uploadPic: editPic,
                paymentAmount: editPrice ? parseFloat(editPrice).toFixed(2) : null,
              }
            : u
        )
      );
      setEditModeId(null);
      Swal.fire({
        title: "Updated!",
        text: "Code updated successfully.",
        icon: "success",
        background: "#0a0a0c",
        color: "#f3f3f5",
      });
      return;
    }
    await updateDoc(doc(db, "Codes", id), {
      title: editTitle,
      uploadPic: editPic,
      paymentAmount: editPrice ? parseFloat(editPrice).toFixed(2) : null,
    });
    setEditModeId(null);
    Swal.fire({
      title: "Updated!",
      text: "Code updated successfully.",
      icon: "success",
      background: "#0a0a0c",
      color: "#f3f3f5",
    });
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!searchRef) return;
    if (query.trim() !== "" && user) {
      try {
        await addDoc(searchRef, {
          query: query.trim(),
          user: user.Email || "Guest",
          timestamp: serverTimestamp(),
        });
      } catch (error) {
        console.error("Error saving search:", error);
      }
    }
  };

  const openFolderModal = (files, title) => {
    setFolderModalFiles(files);
    setFolderTitle(title);
    setShowFolderModal(true);
  };

  return (
    <div className="kk-root cd-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <CodeParticles />

   

      {/* ══════════════ HERO ══════════════ */}
      <header className="cd-hero">
        <div className="cd-hero__bg" aria-hidden="true">
          <span className="cd-hero__grid" data-plx="0.05" />
          <span className="cd-hero__glow cd-hero__glow--acid" data-plx="0.16" />
          <span className="cd-hero__glow cd-hero__glow--violet" data-plx="0.24" />
          <span className="cd-hero__word" data-plx="0.1">
            ARCHIVE
          </span>
        </div>

        <div className="kk-wrap cd-hero__in">
          <motion.div
            className="cd-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="cd-kicker">
              <span className="kk-dot" />
              <span>Code</span>
              <span className="kk-slash">/</span>
              <span>Source</span>
            </div>

            <h1 className="cd-title">
              <motion.span
                className="cd-title__line"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                My <span className="kk-serif kk-acid">Codes</span>
              </motion.span>
              <motion.span
                className="cd-title__sub"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                snippets, folders, shipped.
              </motion.span>
            </h1>

            <p className="cd-lead">
              Some of the code snippets and solutions I&rsquo;ve worked on — single files,
              whole folders, and repos you can open in one tap.
            </p>

            {user && (
              <motion.div
                className="cd-greet"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                👋 Welcome, <strong>{user.Name}</strong>
              </motion.div>
            )}

            <div className="cd-hero__meta">
              <div>
                <b>{uploads.length || 3}</b>
                <span>Items</span>
              </div>
              <div>
                <b>06</b>
                <span>Tags</span>
              </div>
              <div>
                <b>₹99</b>
                <span>From</span>
              </div>
            </div>
          </motion.div>

          <motion.aside
            className="cd-hero__panel"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.16 }}
          >
            <div className="cd-hero__panel-top">
              <span className="cd-hero__panel-dot" />
              <span>Archive Index</span>
              <span className="cd-hero__panel-code">KKS·1436</span>
            </div>

            <div className="cd-hero__panel-num">
              <b>{String(uploads.length || 3).padStart(2, "0")}</b>
              <span>
                code
                <br />
                entries
              </span>
            </div>

            <dl className="cd-hero__panel-rows">
              <div>
                <dt>Delivery</dt>
                <dd>File · Folder · Repo</dd>
              </div>
              <div>
                <dt>Access</dt>
                <dd>Login + UPI</dd>
              </div>
              <div>
                <dt>Download</dt>
                <dd>One click</dd>
              </div>
              <div>
                <dt>Sync</dt>
                <dd>Firestore live</dd>
              </div>
            </dl>
          </motion.aside>
        </div>
      </header>

      {/* ══════════════ TOOLBAR ══════════════ */}
      <div className="cd-bar">
        <div className="kk-wrap cd-bar__in">
          <div className="cd-tabs">
            {CODE_TABS.map((t) => (
              <button
                key={t}
                className={`cd-tab ${activeTab === t ? "is-active" : ""}`}
                onClick={() => setActiveTab(t)}
                type="button"
              >
                {t}
                {t === "All" && <em>{filteredNotes.length}</em>}
              </button>
            ))}
          </div>

          <div className="cd-search">
            <FiSearch className="cd-search__ic" />
            <input
              type="text"
              placeholder="Search code..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
            />
            {searchQuery && (
              <motion.button
                className="cd-search__x"
                onClick={() => setSearchQuery("")}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                type="button"
                aria-label="Clear search"
              >
                <FiX />
              </motion.button>
            )}
          </div>

          <span className="cd-count">
            <FiCode /> {filteredNotes.length} items
          </span>
        </div>
      </div>

      {/* ══════════════ MAIN ══════════════ */}
      <main className="kk-section kk-tight cd-main">
        <div className="kk-wrap">
          {/* Admin form */}
          {isAdmin && (
            <AdminUploadForm
              fileTitle={fileTitle}
              setFileTitle={setFileTitle}
              filePreview={filePreview}
              onFileChange={handleFileChange}
              uploadPic={uploadPic}
              setUploadPic={setUploadPic}
              paymentAmount={paymentAmount}
              setPaymentAmount={setPaymentAmount}
              githubURL={githubURL}
              setGithubURL={setGithubURL}
              onUpload={handleFileUpload}
            />
          )}

          {/* Login warning */}
          {!user && (
            <motion.div
              className="cd-warn"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="cd-warn__ic">
                <FiLock />
              </span>
              <div>
                <b>Login required</b>
                <p>You must log in to access the code files. Browsing is free.</p>
              </div>
              <a className="kk-btn kk-btn--ghost" href="/login">
                Log in <FiArrowUpRight />
              </a>
            </motion.div>
          )}

          {/* Section head */}
          <div className="cd-sec-head">
            <div>
              <span className="kk-eyebrow kk-eyebrow--violet">02 — Archive</span>
              <h2 className="kk-h2">
                Available
                <br />
                <span className="kk-serif">code</span>
              </h2>
            </div>
            <div className="cd-sec-head__aside">
              <p>
                Every entry is a real piece of work — download the file, open the folder,
                or jump straight to the repository.
              </p>
            </div>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="cd-grid">
              {Array.from({ length: 3 }).map((_, i) => (
                <motion.div
                  key={i}
                  className="cd-skel"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.08 }}
                >
                  <div className="cd-skel__img" />
                  <div className="cd-skel__body">
                    <div className="cd-skel__line" />
                    <div className="cd-skel__btn" />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : filteredNotes.length === 0 ? (
            <EmptyState isLoggedIn={!!user} searchQuery={searchQuery} />
          ) : (
            <div className="cd-grid">
              {filteredNotes.map((note, index) => (
                <CodeCard
                  key={note.id}
                  note={note}
                  index={index}
                  user={user}
                  isAdmin={isAdmin}
                  editModeId={editModeId}
                  editTitle={editTitle}
                  editPic={editPic}
                  editPrice={editPrice}
                  onEdit={handleEdit}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={() => setEditModeId(null)}
                  onDelete={deleteUpload}
                  onPay={handlePayNow}
                  onEditChange={handleEditChange}
                  onOpenFolder={openFolderModal}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* ══════════════ CTA ══════════════ */}
      <section className="cd-cta">
        <div className="cd-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>

        <div className="kk-wrap cd-cta__in">
          <span className="kk-eyebrow">Need something else?</span>
          <h2 className="kk-h2">
            Want the code behind{" "}
            <span className="kk-serif kk-acid">a project?</span>
          </h2>
          <p>Ask for any repo, snippet or doubt — I will push it to the archive.</p>

          <div className="cd-cta__btns">
            <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com?subject=Code%20request">
              <FiMail /> Request code
            </a>
            <a
              className="kk-btn kk-btn--ghost kk-btn--lg"
              href="https://github.com/Karanks1436"
              target="_blank"
              rel="noreferrer"
            >
              <FiGithub /> GitHub profile
            </a>
          </div>

          <div className="cd-cta__socials">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target={s.href.startsWith("mailto:") ? undefined : "_blank"}
                rel="noreferrer"
                aria-label={s.label}
                title={s.label}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>
      </section>
   <Navbar />
      <Foot />

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      {/* ═══════════ MODALS ═══════════ */}
      <AnimatePresence>
        {showFolderModal && (
          <FolderModal
            files={folderModalFiles}
            title={folderTitle}
            onClose={() => setShowFolderModal(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPaymentQR && currentPaymentNote && (
          <QRPaymentModal note={currentPaymentNote} onClose={() => setShowPaymentQR(false)} />
        )}
      </AnimatePresence>
    </div>
  );
}
